export interface EnvironmentConfig {
  baseUrl: string
  allowedOrigins: string[]
}

/*
 * Runtime form factors understood by the embeddable shell.
 *
 * This is the browser-bundle copy of the server's source of truth in
 * `src/lib/sdk-form-factor.ts` (`SdkFormFactorValues`). It is duplicated rather
 * than imported because this module ships in the client SDK bundle and must stay
 * free of the zod/db-heavy `experience-design-config` import graph. Keep the two
 * in sync.
 */
export const FormFactors = {
  Embedded: 'embedded',
  Fab: 'fab',
  EmbeddedToSheet: 'embedded-to-sheet',
  CenterBarToDrawer: 'center-bar-to-drawer',
  ExternalTrigger: 'external-trigger',
} as const

export type FormFactor = (typeof FormFactors)[keyof typeof FormFactors]

/*
 * Keep in sync with server re-exports in `src/lib/site-agent/placements/types.ts`
 * and the three-way contract noted in `resolve-placement-shell-config.ts`.
 */
export const SuggestionOrderValues = {
  SuggestionsFirst: 'suggestions-first',
  InputFirst: 'input-first',
} as const

export type SuggestionOrder = (typeof SuggestionOrderValues)[keyof typeof SuggestionOrderValues]

export function resolveSuggestionOrder(order: SuggestionOrder | undefined): SuggestionOrder {
  return order ?? SuggestionOrderValues.SuggestionsFirst
}

interface BaseShellConfig {
  accentColor: string | null
  assistantImageUrl: string | null
  fontFamily: string
  /**
   * Every shell eventually renders a chat input — in the launcher itself, in the
   * panel it opens, or inline — so the placeholder is configurable for all of them.
   */
  placeholderText: string
}

/** Mirror of the server-side `PlacementSdkStartingState`; keep the two in sync. */
export interface ShellStartingState {
  kind?: 'chips' | 'show-stores'
  text: string | null
  options: string[] | null
  /** Show stores only: location tags pre-selected when the finder opens. */
  defaultLocationTags?: string[] | null
}

interface PanelShellConfig extends BaseShellConfig {
  panelTitle: string
  panelImageUrl: string | null
  panelLocation: 'left' | 'right' | 'center'
}

export interface EmbeddedConfig extends BaseShellConfig {
  formFactor: 'embedded'
  startingState?: ShellStartingState
  suggestionOrder?: SuggestionOrder
}

export interface FabConfig extends PanelShellConfig {
  formFactor: 'fab'
  theme?: Record<string, string>
  fabColor: string
  fabIconUrl: string
  startingState?: ShellStartingState
}

export interface EmbeddedToSheetConfig extends PanelShellConfig {
  formFactor: 'embedded-to-sheet'
  startingState: ShellStartingState
  suggestionOrder: SuggestionOrder
  theme: Record<string, string>
  animationStyle: 'ember' | 'nebula' | 'beam' | 'none'
  showInputShadow: boolean
  showPoweredByAnagram: boolean
}

export interface CenterBarToDrawerConfig extends PanelShellConfig {
  formFactor: 'center-bar-to-drawer'
  startingState: ShellStartingState
  theme: Record<string, string>
  animationStyle: 'ember' | 'nebula' | 'beam' | 'none'
  showInputShadow: boolean
  showPoweredByAnagram: boolean
  revealAfterPageScroll: boolean
}

export interface ExternalTriggerConfig extends PanelShellConfig {
  formFactor: 'external-trigger'
  startingState: ShellStartingState
  theme: Record<string, string>
  animationStyle: 'ember' | 'nebula' | 'beam' | 'none'
  showInputShadow: boolean
  showPoweredByAnagram: boolean
}

export type ShellConfig =
  | EmbeddedConfig
  | FabConfig
  | EmbeddedToSheetConfig
  | CenterBarToDrawerConfig
  | ExternalTriggerConfig

export type AnagramAutoSubmitMetadata = {
  source?: 'suggestion'
}

export type AnagramMessage =
  | {type: 'ANAGRAM_READY'; experienceId: string}
  | {type: 'ANAGRAM_RESIZE'; experienceId: string; height: number; allowShrink?: boolean}
  | {type: 'ANAGRAM_START_OVER'; experienceId: string}
  | {type: 'ANAGRAM_SCROLL'; deltaY: number; deltaMode: number}
  | {type: 'ANAGRAM_TOUCH_START'; clientY: number | null}
  | {type: 'ANAGRAM_TOUCH_MOVE'; clientY: number | null}
  | {type: 'ANAGRAM_TOUCH_END'}
  | {type: 'ANAGRAM_ACTIVITY'; key: string; value: {experienceId: string; lastUsedDate: string}}
  | {type: 'ANAGRAM_CLOSE_REQUESTED'; experienceId: string}
  | {type: 'ANAGRAM_AUTO_SUBMIT'; text: string; metadata?: AnagramAutoSubmitMetadata}
  | {type: 'ANAGRAM_FOCUS_INPUT'}
  | {type: 'ANAGRAM_OPEN_URL'; url: string}
